import styled from "styled-components";
import { theme } from "../theme";

export const MessagingContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: ${theme.colors.background.primary};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    height: 100vh;
    height: calc(var(--vh, 1vh) * 100); /* iOS Safari fix */
  }
`;

export const MessagingHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.md};
  border-bottom: 1px solid ${theme.colors.border};
  background: ${theme.colors.background.primary};
  box-shadow: ${theme.shadows.light};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  width: 100%;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    min-height: 60px;
  }

  /* Make room for the persistent desktop sidebar so the header text
     (e.g. "Message Notifications") isn't hidden behind it. */
  @media (min-width: calc(${theme.breakpoints.tablet} + 1px)) {
    left: 88px;
    width: calc(100% - 88px);
  }
`;

export const MessagingHeaderTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin: 0;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.25rem;
  }
`;

export const MessagingHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

export const DashboardButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: ${theme.borderRadius.sm};
  color: ${theme.colors.text.light};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  font-size: 0.8rem;
  cursor: pointer;
  font-weight: 600;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    font-size: 0.75rem;
  }
`;

export const MessagingContent = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  position: relative;
  /* Add top padding to account for fixed header */
  padding-top: 80px;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    padding-top: 70px;
  }
`;

export const MessagingSidebar = styled.div<{ $isVisible?: boolean }>`
  width: 320px;
  border-right: 1px solid ${theme.colors.border};
  background: ${theme.colors.background.secondary};
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  /* Position sidebar to start below fixed header */
  margin-top: -80px;
  padding-top: 80px;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 280px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 200;
    transform: ${props => props.$isVisible ? 'translateX(0)' : 'translateX(-100%)'};
    transition: transform ${theme.transitions.normal};
    border-right: none;
    border-bottom: none;
    margin-top: 0;
    padding-top: 70px;
  }
`;

export const MessagingMain = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: ${theme.colors.background.primary};
  min-width: 0; /* Prevents flex item from overflowing */
  /* Position main content to start below fixed header */
  margin-top: -80px;
  padding-top: 80px;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 100%;
    height: 100%;
    position: relative;
    z-index: 100;
    margin-top: 0;
    padding-top: 70px;
  }
`;

export const MobileBackButton = styled.button`
  display: none;
  padding: ${theme.spacing.sm};
  border: none;
  background: transparent;
  color: ${theme.colors.text.primary};
  cursor: pointer;
  border-radius: ${theme.borderRadius.sm};
  margin-right: ${theme.spacing.sm};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  &:hover {
    background: ${theme.colors.background.secondary};
  }
`;

export const MobileOverlay = styled.div<{ $isVisible: boolean }>`
  display: none;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    display: ${props => props.$isVisible ? 'block' : 'none'};
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    z-index: 150;
  }
`;

export const MessagingLoading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${theme.colors.text.secondary};
  font-size: 1rem;
`;

export const MessagingError = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${theme.colors.error};
  text-align: center;
  padding: ${theme.spacing.xl};
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${theme.colors.text.secondary};
  text-align: center;
  padding: ${theme.spacing.xl};
  background: ${theme.colors.background.primary};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    display: none; /* Hide empty state on mobile */
  }
`;

export const EmptyStateIcon = styled.div`
  font-size: 4rem;
  margin-bottom: ${theme.spacing.md};
  opacity: 0.5;
`;

export const EmptyStateTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

export const EmptyStateDescription = styled.p`
  font-size: 1rem;
  color: ${theme.colors.text.secondary};
  margin: 0;
  max-width: 400px;
`;
